/**
 * Business Hours Utilities
 *
 * Helper functions for formatting and displaying business hours.
 */

import { formatBusinessTime } from './business-time';

export interface BusinessHour {
  day_of_week: number; // 1-7 (Monday-Sunday)
  is_open: boolean;
  open_time?: string | null;
  close_time?: string | null;
}

/**
 * Format a business hour entry for display
 * @param hour - Business hour object
 * @param timezone - Business timezone
 * @param t - Translation function for day names
 * @returns Formatted string (e.g., "Monday: 09:00 - 17:00" or "Monday: Closed")
 */
export const formatBusinessHour = (
  hour: BusinessHour,
  timezone: string,
  t: (key: string) => string
): string => {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  let dayIndex = hour.day_of_week - 1;

  if (dayIndex < 0 || dayIndex >= days.length) {
    dayIndex = Math.max(0, Math.min(dayIndex, 6));
  }

  const dayName = t(`days.${days[dayIndex]}`);

  if (!hour.is_open) {
    return `${dayName}: ${t('storefront.closed')}`;
  }

  if (hour.open_time && hour.close_time) {
    const openTime = formatBusinessTime(hour.open_time, timezone, 'HH:mm');
    const closeTime = formatBusinessTime(hour.close_time, timezone, 'HH:mm');
    return `${dayName}: ${openTime} - ${closeTime}`;
  }

  return `${dayName}: ${t('storefront.closed')}`;
};

/**
 * Group business hours by time periods
 * @param hours - Array of business hours
 * @returns Grouped hours with consecutive days having the same hours
 */
export const groupBusinessHours = (hours: BusinessHour[]): string[] => {
  // TODO: Implement smart grouping (e.g., "Mon-Fri: 09:00 - 17:00")
  return [];
};
